/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Navbar — menu theo PH đang mở.
 * Logic: activePhanHe = route || Recoil; include*Menus = license ∩ availablePhanHe ∩ activePhanHe.
 * hasRole: filterListRoleByPhanHe trước khi check.
 * getProfile: logo → sync rồi /intro (đổi PH). Không getProfile khi chuyển trang menu.
 * (F5: Layout root sync 1 lần khi mount.)
 */
import React, { useCallback, useEffect, useMemo, useState, useRef, useTransition } from 'react';
import { useSuDungBackupBaoCao } from '@/hooks/useSuDungBackupBaoCao';
import type { MenuProps } from 'antd';
import { Button, Menu, message } from 'antd';
import { useRecoilState, useRecoilValue } from 'recoil';
import { menu } from '@/states/menu';
import Link from 'next/link';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useAuthStore } from '@/store/useAuthStore';
import { ChevronLeft, ChevronRight, GuideIco, ListLibIco, MailIco, RetweetIco, RobotIco, RocketIco, Setting2Ico, TipIco } from '../Icons/IconMenu';
import { useClearCache } from '@/hooks/useClearCache';
import {
    BangKeHoachIcon,
    ChartIcon,
    DaoTaoIcon,
    DatabaseIcon,
    LicenseIcon,
    UsersGroupIcon,
} from '../Icons/NavbarIcon';
// useSignalR removed - using RealtimeStore for data invalidation
import { apiUrlApi } from '@/services/api-sync/sync-api';
import { License } from '@/types/license';
import {
    isLicenseDemo,
    showDemoAccountBlockedModal,
} from '@/utils/demoAccount';
import { useRealtimeStore } from '../../store/RealtimeStore';
import IntlMessages from '../Locale/IntlMessages';
import { setMenuTransitioning } from '@/utils/menuTransitioning';
import { useTranslation } from 'react-i18next';
import { BscStepMenus } from './BscStep';
import { CultureStep, LibraryStep } from './CultureStep';
import './navbar-menu.css';
import { syncNavbarMenuKeysFromPathname } from './navbarMenuRouteSync';
import { AI_AGENT_MENU_ITEM, AI_HUB_SETTINGS_SECTIONS, aiHubSettingsMenuKey, aiHubSettingsPath } from '@/config/aiAgentMenu';
import { useActiveAiGate } from '@/components/common/aiAssistant/useActiveAiGate';
import { canManageKnowledge, canUseAiCoaching } from '@/components/common/AiHub/datastudio/aiKbAccess';
import { useAiHubMe } from '@/components/common/AiHub/useAiHubMe';
import { buildBscCauHinhEmailChildren, buildVanHoaCauHinhEmailChildren } from './CauHinhEmailMenu';
import { phanHe } from '@/states/authen';
import { ProcedureStep, QuyTrinhCauHinhEmailMenu } from './ProcedureStep';
import VersionBadge from './versionBadge';
import { CONSTANTS } from '@/utils/constants';
import { ROUTES } from '@/utils/pathRoutes';
import { canShowActionPlanV2Menu } from '@/components/common/ActionPlanV2/constants';
import { useActionPlanV2Enabled } from '@/hooks/useActionPlanV2Enabled';
import { syncLoggedInUserPermissionsAfterRoleGroupChange } from '@/utils/organizationIndexedDBUtils';

import {
    clearLicenseStorage,
    getLicenseInitParam,
    readLicenseFromStorage,
    saveLicenseToStorage,
} from '@/utils/licenseStorage';
import { resolvePhanHeFromRoute } from '@/utils/phanHeStorage';
import { filterListRoleByPhanHe, hasAvailablePhanHe } from '@/utils/phanHeRoles';
import {
    matchesQuyTrinhLicense,
    matchesVanHoaLicense,
    roleMatchesMenuModulesExact,
    roleMatchesVanHoaMenuModules,
} from '@/utils/vanHoaPermission';
import { useCustomerLogoHeader } from '@/hooks/useCustomerLogoHeader';
import { useCanhBaoKPIBaKyLienTiepEnabled } from '@/hooks/useCanhBaoKPIBaKyLienTiepEnabled';

const LICENSE_SETUP_PATH = '/thiet-lap/license';
const pendingLicenseRequests = new Map<string, Promise<any>>();

const getLicenseOnce = (tenantID: string, force = false) => {
    if (!force) {
        const pending = pendingLicenseRequests.get(tenantID);
        if (pending) return pending;
    } else {
        pendingLicenseRequests.delete(tenantID);
    }

    const request = apiUrlApi.getLicense(getLicenseInitParam(tenantID)).finally(() => {
        if (pendingLicenseRequests.get(tenantID) === request) {
            pendingLicenseRequests.delete(tenantID);
        }
    });
    pendingLicenseRequests.set(tenantID, request);
    return request;
};

const resolveLicenseModules = (licenseData: License, phanHe: string) => {
    if (phanHe === CONSTANTS.PHAN_HE.VAN_HOA) {
        return licenseData?.vhFunction?.split(',').map((item: string) => item.trim()) || [];
    }
    if (phanHe === CONSTANTS.PHAN_HE.QUY_TRINH) {
        return licenseData?.qtFunction?.split(',').map((item: string) => item.trim()) || [];
    }
    if (phanHe === CONSTANTS.PHAN_HE.BSC) {
        return licenseData?.bscFunction?.split(',').map((item: string) => item.trim()) || [];
    }
    return [];
};

const isOnLicenseSetupPage = (pathname: string) =>
    pathname === LICENSE_SETUP_PATH || pathname.startsWith(`${LICENSE_SETUP_PATH}/`);

const getLicenseKey = (license: License) => {
    const raw = license as License & { Key?: string };
    return (raw.key || raw.Key || '').trim();
};

const isLicenseActive = (license?: License | null): boolean => {
    if (!license) return false;
    if (!getLicenseKey(license)) return false;
    if (!license.status) return false;
    if (!license.actived) return false;
    return true;
};

const toNonNavigableLabel = (label: React.ReactNode): React.ReactNode => {
    if (!React.isValidElement(label)) return label;

    const props = label.props as { href?: string; className?: string; children?: React.ReactNode };
    if (label.type === Link || props.href) {
        return <span className={props.className}>{props.children}</span>;
    }

    return label;
};

const disableAlertDemoNavigation = (items: MenuItem[], isDemo: boolean): MenuItem[] => {
    if (!isDemo) return items;

    return items.map((item) => {
        if (!item || typeof item !== 'object') return item;

        const menuItem = item as MenuItem & { alertDemo?: boolean; label?: React.ReactNode; children?: MenuItem[] };
        const nextItem = { ...menuItem } as MenuItem & { label?: React.ReactNode; children?: MenuItem[] };

        if (menuItem.alertDemo && menuItem.label) {
            nextItem.label = toNonNavigableLabel(menuItem.label);
        }

        if (menuItem.children?.length) {
            nextItem.children = disableAlertDemoNavigation(menuItem.children, isDemo);
        }

        return nextItem as MenuItem;
    });
};

type BaseMenuItem = Required<MenuProps>['items'][number];
type MenuItem = BaseMenuItem & {
    modulename?: string;
    checklicense?: boolean;
    alertDemo?: boolean;
    children?: MenuItem[];
};

const collectAlertDemoKeys = (items: MenuItem[]): Set<string> => {
    const keys = new Set<string>();

    const walk = (nodes: MenuItem[]) => {
        nodes.forEach((item) => {
            if (!item || typeof item !== 'object') return;

            const menuItem = item as MenuItem & { alertDemo?: boolean; key?: string };
            if (menuItem.key && menuItem.alertDemo) {
                keys.add(String(menuItem.key));
            }
            if (menuItem.children?.length) {
                walk(menuItem.children);
            }
        });
    };

    walk(items);
    return keys;
};

const stripAlertDemoProp = (items: MenuItem[]): MenuItem[] => {
    return items.map((item) => {
        if (!item || typeof item !== 'object') return item;

        const menuItem = item as MenuItem;
        const nextItem = { ...menuItem } as MenuItem;
        delete (nextItem as { alertDemo?: boolean }).alertDemo;

        if (menuItem.children?.length) {
            nextItem.children = stripAlertDemoProp(menuItem.children);
        }

        return nextItem;
    });
};

const moduleScopedHref = (includeVanHoaMenus: boolean, href: string) =>
    includeVanHoaMenus ? `/van-hoa${href}` : href;

const buildNghiPhepMenuItem = (): MenuItem => ({
    key: 'nghi-phep',
    modulename: 'nghidaihan',
    checklicense: true,
    label: (
        <Link href="/to-chuc/nghi-phep">
            <IntlMessages id="bsc.menu.nghi-dai-han" text="Nghỉ dài hạn" />
        </Link>
    ),
});

const buildCoCauToChucChildren = (includeVanHoaMenus: boolean, includeQuyTrinh: boolean): MenuItem[] => {
    const orgItems: MenuItem[] = [
        {
            key: 'phong-ban',
            modulename: 'bophan,thietlap.bophan,vanhoa_bophan',
            checklicense: true,
            label: (
                <Link href={moduleScopedHref(includeVanHoaMenus, '/to-chuc/bo-phan')}>
                    <IntlMessages id="bsc.menu.danh-sach-bo-phan" text="Phòng ban" />
                </Link>
            ),
        },
        {
            key: 'chuc-vu',
            modulename: 'chucvu,thietlap.chucvu,vanhoa_chucvu',
            checklicense: true,
            label: (
                <Link href={moduleScopedHref(includeVanHoaMenus, '/to-chuc/chuc-vu')}>
                    <IntlMessages id="bsc.menu.danh-sach-chuc-vu" text="Chức vụ" />
                </Link>
            ),
        },
        {
            key: 'nhan-vien',
            modulename: 'nhanvien,thietlap.nhanvien,vanhoa_nhanvien',
            checklicense: true,
            label: (
                <Link href={moduleScopedHref(includeVanHoaMenus, '/to-chuc/nhan-vien')}>
                    <IntlMessages id="bsc.menu.danh-sach-nhan-vien" text="Nhân viên" />
                </Link>
            ),
        },
    ];

    const soDoToChucItem: MenuItem = {
        key: 'so-do-to-chuc',
        modulename: 'chucvu,thietlap.chucvu,vanhoa_chucvu',
        checklicense: true,
        label: (
            <Link href="/quy-trinh/so-do-to-chuc">
                <IntlMessages id="bsc.menu.so-do-to-chuc" text="Sơ đồ tổ chức" />
            </Link>
        ),
    };

    if (includeVanHoaMenus) {
        return [
            ...orgItems,
            {
                key: 'nhom-quyen',
                modulename: 'nhomquyen',
                checklicense: false,
                label: (
                    <Link href={moduleScopedHref(includeVanHoaMenus, '/role-group')}>
                        <IntlMessages id="bsc.menu.nhom-quyen" text="Nhóm quyền" />
                    </Link>
                ),
            },
        ];
    }

    return [
        ...orgItems,
        ...(includeQuyTrinh ? ([soDoToChucItem] as MenuItem[]) : []),
        buildNghiPhepMenuItem(),
        {
            key: 'nhom-quyen',
            modulename: 'nhomquyen',
            checklicense: false,
            label: (
                <Link href={moduleScopedHref(false, '/role-group')}>
                    <IntlMessages id="bsc.menu.nhom-quyen" text="Nhóm quyền" />
                </Link>
            ),
        },
        ...(!includeQuyTrinh
            ? ([
                {
                    key: 'quyen-xet-duyet',
                    modulename: 'roleapprove',
                    checklicense: false,
                    alertDemo: true,
                    label: (
                        <Link href="/role-approve">
                            <IntlMessages id="bsc.menu.quyen-xet-duyet" text="Quyền xét duyệt" />
                        </Link>
                    ),
                },
            ] as MenuItem[])
            : []),
    ];
};

// const baseItemsQuyTrinh = [ProcedureStep] as MenuItem[];
/** AI Coaching — menu cấp 1 ngay dưới «Thiết lập ngôn ngữ» khi AI đang bật. Admin TOPPION HOLDING / email trong whitelist
 * (`canUseAiCoaching` → isAdmin) thấy mọi mục; tài khoản site nền tảng thấy thêm Data Studio; mọi người thấy MCP. */
const aiAgentSettingsMenu = (showKnowledge: boolean, isAdmin: boolean) => ({
    key: AI_AGENT_MENU_ITEM.key,
    checklicense: false,
    icon: (
        <span>
            <RobotIco />
        </span>
    ),
    label: <IntlMessages id={AI_AGENT_MENU_ITEM.i18nId} text={AI_AGENT_MENU_ITEM.text} />,
    children: AI_HUB_SETTINGS_SECTIONS.filter((s) => (s.everyone ? true : s.platformOnly ? showKnowledge || isAdmin : isAdmin)).map((s) => ({
        key: aiHubSettingsMenuKey(s.key),
        checklicense: false,
        // shallow: đổi mục trong Cài đặt AI Agent chỉ đổi nội dung (cùng trang), không chạy lại vòng đời route
        // Không phải site nền tảng: mục kho tri thức là kho riêng của công ty (TenantSourcesPanel), không phải Data Studio chung
        label: <Link href={aiHubSettingsPath(s.key)} shallow>{s.key === 'knowledge' && !showKnowledge ? 'Nguồn của công ty' : s.title}</Link>,
    })),
}) as MenuItem;

const baseItems = (
    isAdmin: boolean,
    includeVanHoaMenus: boolean,
    includeBscMenus: boolean,
    includeQuyTrinh: boolean,
    isAdminQuyTrinh: boolean,
    showAiKnowledge = false,
    activeAI = false,
    aiCoaching = false,
    suDungBackupBaoCao = false,
) => [
    ...(includeQuyTrinh ? ([ProcedureStep] as MenuItem[]) : []),
    ...(includeVanHoaMenus || includeBscMenus
        ? ([
            {
                key: 'overview',
                label: <IntlMessages id="bsc.menu.tong-quan" text="Tổng quan" />,
                type: 'group',
                children: [
                    ...(includeBscMenus
                        ? ([
                            {
                                key: 'bangkehoach',
                                // modulename: 'bangkehoach',
                                // checklicense: false,
                                label: (
                                    <Link className="uppercase" href="/bang-ke-hoach">
                                        <IntlMessages id="bsc.menu.bang-ke-hoach" text="Bảng kế hoạch" />
                                    </Link>
                                ),
                                icon: (
                                    <span>
                                        <BangKeHoachIcon />
                                    </span>
                                ),
                            },
                        ] as MenuItem[])
                        : []),
                    {
                        key: 'dotontap',
                        modulename: includeVanHoaMenus ? 'daotaovanhoa,vanhoa_dotontap' : 'daotaobsc,dotontap',
                        checklicense: true,
                        alertDemo: true,
                        label: (
                            <Link className="uppercase" href={includeVanHoaMenus ? '/van-hoa/dot-on-tap' : '/dot-on-tap'}>
                                {includeVanHoaMenus ? <IntlMessages id="bsc.menu.daotaovanhoa" text="Đào tạo Văn hóa" /> : <IntlMessages id="bsc.menu.đao-tao-bsc" text="Đào tạo BSC" />}
                            </Link>
                        ),
                        icon: (
                            <span>
                                <DaoTaoIcon />
                            </span>
                        ),
                    },
                    {
                        key: 'dashboard',
                        modulename: includeVanHoaMenus ? 'dashboard,vanhoa_dashboard' : 'dashboard',
                        checklicense: true,
                        label: (
                            <Link className="uppercase" href={includeVanHoaMenus ? '/van-hoa/dashboard' : '/dashboard'}>
                                <IntlMessages id="bsc.menu.dashboard" text="Dashboard" />
                            </Link>
                        ),
                        icon: (
                            <span>
                                <ChartIcon />
                            </span>
                        ),
                    },
                    // Tạm thời ẩn menu họp tuần
                    // {
                    //     key: 'hoptuan',
                    //     modulename: 'hoptuan',
                    //     checklicense: true,
                    //     alertDemo: true,
                    //     label: (
                    //         <Link className="uppercase" href="/hop-tuan">
                    //             Họp tuần
                    //         </Link>
                    //     ),
                    //     icon: (
                    //         <span>
                    //             <HopTuanIcon />
                    //         </span>
                    //     ),
                    // },
                ],
            },
        ])
        : []),
    ...(includeVanHoaMenus ? ([CultureStep, LibraryStep] as MenuItem[]) : []),
    ...(includeBscMenus ? (BscStepMenus as MenuItem[]) : []),
    ...(includeVanHoaMenus || includeBscMenus || includeQuyTrinh
        ? ([
            // Setting & Guide
            {
                key: 'setting',
                label: <IntlMessages id="bsc.menu.thiet-lap" text="Thiết Lập" />,
                type: 'group',
                children: [
                    ...(includeVanHoaMenus
                        ? ([
                            {
                                key: 'vanHoathangDo',
                                label: <IntlMessages id="bsc.menu.van-hoa-thang-do" text="Thang đo văn hóa" />,
                                icon: (
                                    <span>
                                        <ListLibIco />
                                    </span>
                                ),
                                children: [
                                    {
                                        key: 'thoigiankhaosat',
                                        modulename: 'vanhoa_thangdovanhoa,vanhoa_thangdo,thangdo.vanhoa',
                                        checklicense: true,
                                        label: (
                                            <Link href="/van-hoa/cau-hinh/thoigiankhaosat">
                                                <IntlMessages
                                                    id="bsc.menu.van-hoa-thang-do"
                                                    text="Giới hạn thời gian làm bài khảo sát"
                                                />
                                            </Link>
                                        ),
                                    },
                                    {
                                        key: 'thangdo-danhgia',
                                        modulename: 'vanhoa_thangdovanhoa,vanhoa_thangdo,thangdo.vanhoa',
                                        checklicense: true,
                                        label:
                                            <Link href="/van-hoa/thang-do">
                                                <IntlMessages id="bsc.menu.van-hoa-thang-do" text="Thang đo đánh giá" />
                                            </Link>,
                                    },
                                    {
                                        key: 'thangdo-congviec',
                                        modulename: 'vanhoa_thangdovanhoa,vanhoa_thangdo,thangdo.vanhoa',
                                        checklicense: true,
                                        label:
                                            <Link href="/van-hoa/don-vi">
                                                <IntlMessages id="bsc.menu.van-hoa-thang-do" text="Thang đo công việc" />
                                            </Link>,
                                    },
                                ],
                            },
                            {
                                key: 'van-hoa-cau-hinh',
                                modulename: 'cauhinh',
                                checklicense: false,
                                label: (
                                    <Link href={moduleScopedHref(includeVanHoaMenus, '/cau-hinh')}>
                                        <IntlMessages id="bsc.menu.cauhinhlogo" text="Cấu hình logo tiêu đề" />
                                    </Link>
                                ),
                                icon: (
                                    <span>
                                        <Setting2Ico />
                                    </span>
                                ),
                            },
                        ] as MenuItem[])
                        : []),
                    {
                        key: 'sub1',
                        label: <IntlMessages id="bsc.menu.co-cau-to-chuc" text="Cơ cấu tổ chức" />,
                        icon: (
                            <span>
                                <UsersGroupIcon />
                            </span>
                        ),
                        children: buildCoCauToChucChildren(includeVanHoaMenus, includeQuyTrinh),
                    },
                    ...(includeQuyTrinh
                        ? ([{
                            key: 'quy-trinh-quan-ly-bieu-mau',
                            checklicense: false,
                            label: (
                                <Link href="/quy-trinh/quan-ly-bieu-mau">
                                    <IntlMessages id="bsc.menu.quan-ly-bieu-mau" text="Quản lý biểu mẫu" />
                                </Link>
                            ),
                            icon: <span><ListLibIco /></span>,
                        }] as MenuItem[])
                        : []),
                    ...(includeQuyTrinh && isAdminQuyTrinh
                        ? ([{
                            key: 'quy-trinh-ai-rules',
                            modulename: 'buoc2ngoinha',
                            checklicense: false,
                            label: <Link href="/quy-trinh/ai-rules">Quy tắc AI</Link>,
                            icon: <span><Setting2Ico /></span>,
                        }] as MenuItem[])
                        : []),
                    // QT: chỉ menu email quy trình. BSC/VH: menu email tương ứng (tránh double trên /quy-trinh/).
                    ...(includeQuyTrinh
                        ? ([QuyTrinhCauHinhEmailMenu] as MenuItem[])
                        : ([
                            {
                                key: 'sub8',
                                label: <IntlMessages id="bsc.label.cauhinhemail" text="Cấu hình Email" />,
                                modulename: 'cauhinh_template',
                                icon: (
                                    <span>
                                        <MailIco />
                                    </span>
                                ),
                                children: includeVanHoaMenus
                                    ? buildVanHoaCauHinhEmailChildren()
                                    : buildBscCauHinhEmailChildren(),
                            },
                        ] as MenuItem[])),
                    // {
                    //     key: 'thong-bao-he-thong',
                    //     modulename: 'cauhinh',
                    //     checklicense: false,
                    //     alertDemo: true,
                    //     label: (
                    //         <Link href="/thiet-lap/thong-bao-he-thong">
                    //             <IntlMessages id="bsc.menu.quan-tri-he-thong" text="Quản trị hệ thống" />
                    //         </Link>
                    //     ),
                    //     icon: (
                    //         <span>
                    //             <Setting2Ico />
                    //         </span>
                    //     ),
                    // },
                    ...(includeVanHoaMenus ? ([
                        {
                            key: 'van-hoa-logfiles',
                            modulename: 'logfiles',
                            checklicense: false,
                            alertDemo: true,
                            label: (
                                <Link href={ROUTES.VAN_HOA.LOGFILES}>
                                    <IntlMessages id="bsc.label.logfile" text="File Logs" />
                                </Link>
                            ),
                            icon: (
                                <span>
                                    <LicenseIcon />
                                </span>
                            ),
                        },
                        {
                            key: 'van-hoa-huong-dan',
                            alertDemo: true,
                            label: (
                                <Link href="/van-hoa/huong-dan">
                                    <IntlMessages id="bsc.label.huongdansudung" text="Hướng dẫn sử dụng" />
                                </Link>
                            ),
                            icon: <span><TipIco /></span>,
                        },
                    ] as MenuItem[]) : []),
                    ...(!includeQuyTrinh && !includeVanHoaMenus ? ([
                        {
                            key: 'sub5',
                            label: <IntlMessages id="bsc.label.thuvienkpi" text="Thư viện KPIs" />,
                            icon: (
                                <span>
                                    <ListLibIco />
                                </span>
                            ),
                            children: [
                                {
                                    key: 'kpi-chung',
                                    modulename: 'kpichung',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/kpi-chung">
                                            <IntlMessages id="bsc.label.kpichung" text="KPIs Chung" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'kpi-ngoai-le',
                                    modulename: 'ngoaile',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/kpi-ngoai-le">
                                            <IntlMessages id="bsc.label.kpisngoaile" text="KPIs ngoại lệ" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'kpiipoo',
                                    modulename: 'kpiipoo',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/kpiipoo">
                                            <IntlMessages id="bsc.label.kpisipoo" text="KPIs IPOO" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'thu-vien-kpi',
                                    modulename: 'thuvienkpi',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/thu-vien-kpi">
                                            <IntlMessages id="bsc.label.danhsachkpis" text="Danh sách KPIs" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'hieu-suat-kpi',
                                    modulename: 'hieusuatkpi',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/hieu-suat-kpi">
                                            <IntlMessages id="bsc.menu.ngat-hieu-suat-kpi" text="Ngắt hiệu suất KPI" />
                                        </Link>
                                    ),
                                },
                            ],
                        },
                    ] as MenuItem[]) : []),
                    ...(!includeQuyTrinh && !includeVanHoaMenus ? ([
                        {
                            key: 'sub6',
                            label: <IntlMessages id="bsc.label.cauhinh" text="Cấu hình" />,
                            icon: (
                                <span>
                                    <Setting2Ico />
                                </span>
                            ),
                            children: [
                                {
                                    key: 'giai-doan',
                                    modulename: 'giaidoan',
                                    alertDemo: true,
                                    checklicense: false,
                                    label: (
                                        <Link href="/giai-doan">
                                            <IntlMessages id="bsc.label.giaidoan" text="Giai đoạn" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'kpi-cong-thuc',
                                    modulename: 'kpicongthuc',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/kpi-cong-thuc">
                                            <IntlMessages id="bsc.label.congthuckpi" text="Công thức KPI" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'cau-hinh-chung',
                                    modulename: 'cauhinh',
                                    alertDemo: true,
                                    checklicense: false,
                                    label: (
                                        <Link href="/cau-hinh-chung">
                                            <IntlMessages id="bsc.label.cauhinhtoancuc" text="Cấu hình toàn cục" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'cau-hinh-license',
                                    modulename: 'license',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/thiet-lap/license">
                                            <IntlMessages id="bsc.menu.thiet-lap-license" text="Thiết lập license" />
                                        </Link>
                                    ),
                                },
                            ],
                        },

                        // Theo rule ẩn đi, không cho phép user truy cập
                        // {
                        //   key: 'sub4',
                        //   label: 'Hướng dẫn',
                        //   icon: (
                        //     <span>
                        //       <GuideIco />
                        //     </span>
                        //   ),
                        //   children: [
                        //     { key: 'kien-thuc-loai', label: <Link href="/kien-thuc-loai">Kiến thức loại</Link> }, // Theo rule ẩn đi, không cho phép user truy cập
                        //     { key: 'huong-dan-admin', label: <Link href="/huong-dan-admin">Quản lý hướng dẫn</Link> }, // Theo rule ẩn đi, không cho phép user truy cập
                        //     { key: 'cau-hinh', label: <Link href="/thiet-lap/cau-hinh">Cấu hình hệ thống</Link> }, // Theo rule ẩn đi, không cho phép user truy cập
                        //   ],
                        // },
                        {
                            key: 'don-vi-do-luong',
                            modulename: 'donvi,thietlap.donvi',
                            checklicense: true,
                            alertDemo: true,
                            label: (
                                <Link href={moduleScopedHref(includeVanHoaMenus, '/thiet-lap/don-vi-do-luong')}>
                                    <IntlMessages id="bsc.menu.danh-sach-don-vi" text="Đơn vị đo lường" />
                                </Link>
                            ),
                            icon: (
                                <span>
                                    <GuideIco />{' '}
                                </span>
                            ),
                        },
                        {
                            key: 'chuyen-du-lieu',
                            modulename: 'copydata,chuyendulieu',
                            checklicense: false,
                            alertDemo: true,
                            label: (
                                <Link href="/chuyen-du-lieu">
                                    <IntlMessages id="bsc.label.chuyendulieu" text="Chuyển dữ liệu" />
                                </Link>
                            ),
                            icon: (
                                <span>
                                    <RetweetIco />
                                </span>
                            ),
                        },
                        {
                            key: 'api',
                            label: <IntlMessages id="bsc.label.thietlapapi" text="Thiết lập API" />,
                            icon: (
                                <span>
                                    <RocketIco />
                                </span>
                            ),
                            children: [
                                {
                                    key: 'api-sync-data',
                                    modulename: 'apiurl',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href={moduleScopedHref(includeVanHoaMenus, '/thiet-lap/api-sync-data')}>
                                            <IntlMessages
                                                id="bsc.menu.api-sync-data"
                                                text="API đồng bộ dữ liệu"
                                            />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'api-co-cau',
                                    modulename: 'apisyncurl',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href={moduleScopedHref(includeVanHoaMenus, '/thiet-lap/api-co-cau')}>
                                            <IntlMessages id="bsc.menu.api-co-cau-to-chuc" text="API cơ cấu tổ chức" />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'api-co-cau-thong-bao',
                                    modulename: 'apisyncurl',
                                    checklicense: false,
                                    alertDemo: true,
                                    label: (
                                        <Link href={moduleScopedHref(includeVanHoaMenus, '/thiet-lap/api-co-cau/thong-bao')}>
                                            <IntlMessages
                                                id="bsc.menu.api-co-cau-thong-bao"
                                                text="Thông báo đồng bộ cơ cấu"
                                            />
                                        </Link>
                                    ),
                                },
                                {
                                    key: 'api-url-whitelist',
                                    modulename: 'apiurlwhitelist',
                                    checklicense: false,
                                    label: (
                                        <Link href="/thiet-lap/api-url-whitelist">
                                            <IntlMessages
                                                id="bsc.menu.api-url-whitelist"
                                                text="Whitelist API URL"
                                            />
                                        </Link>
                                    ),
                                },
                            ]
                        },
                        {
                            key: 'ngon-ngu',
                            label: <IntlMessages id="bsc.menu.thiet-lap-ngon-ngu" text="Thiết lập ngôn ngữ" />,
                            icon: (
                                <span>
                                    <RocketIco />
                                </span>
                            ),
                            children: [
                                {
                                    key: 'languages',
                                    modulename: 'languages',
                                    checklicense: false,
                                    label: (
                                        <Link href={moduleScopedHref(includeVanHoaMenus, '/thiet-lap/languages')}>
                                            <IntlMessages id="bsc.label.ngonngu" text="Ngôn ngữ" />
                                        </Link>
                                    ),
                                },
                                // {
                                //     key: 'resources',
                                //     modulename: 'resources',
                                //     checklicense: false,
                                //     label: <Link href="/thiet-lap/resources">Tài nguyên</Link>,
                                // },
                            ]
                        },
                        // AI Coaching: site đã bật AI (config_setting_active_ai) + Admin TOPPION HOLDING hoặc whitelist
                        ...(activeAI ? [aiAgentSettingsMenu(showAiKnowledge, aiCoaching)] : []),
                        // {
                        //     key: 'sub8',
                        //     label: <IntlMessages id="bsc.label.cauhinhemail" text="Cấu hình Email" />,
                        //     icon: (
                        //         <span>
                        //             <MailIco />
                        //         </span>
                        //     ),
                        //     children: includeVanHoaMenus
                        //         ? buildVanHoaCauHinhEmailChildren()
                        //         : buildBscCauHinhEmailChildren(),
                        // },
                    ] as MenuItem[]) : []),
                ],

            },
            ...(!includeQuyTrinh && !includeVanHoaMenus ? ([
                // Backup & Restore
                {
                    key: 'backup',
                    label: <IntlMessages id="api.notification.type.system" text="Hệ thống" />,
                    type: 'group',
                    children: [
                        {
                            key: 'database',
                            modulename: 'database.backup,cauhinh.backupdatabase',
                            checklicense: true,
                            alertDemo: true,
                            label: (
                                <Link href="/database">
                                    <IntlMessages id="bsc.label.database" text="Database" />
                                </Link>
                            ),
                            icon: (
                                <span>
                                    <DatabaseIcon />
                                </span>
                            ),
                        },
                        // Cấu hình › Sử dụng Backup báo cáo (mặc định tắt → ẩn)
                        ...(suDungBackupBaoCao
                            ? ([
                                {
                                    key: 'backup-bao-cao',
                                    modulename: 'buoc11,baocao.baocaokpi',
                                    checklicense: true,
                                    alertDemo: true,
                                    label: (
                                        <Link href="/backup-bao-cao">
                                            <IntlMessages id="bsc.label.backup_baocao" text="Backup báo cáo" />
                                        </Link>
                                    ),
                                    icon: (
                                        <span>
                                            <DatabaseIcon />
                                        </span>
                                    ),
                                },
                              ] as MenuItem[])
                            : []),
                        {
                            key: 'logfiles',
                            modulename: 'logfiles',
                            checklicense: false,
                            alertDemo: true,
                            label: (
                                <Link href="/logfiles">
                                    <IntlMessages id="bsc.label.logfile" text="File Logs" />
                                </Link>
                            ),
                            icon: (
                                <span>
                                    <LicenseIcon />
                                </span>
                            ),
                        },
                        {
                            key: 'huong-dan',
                            alertDemo: true,
                            label: (
                                <Link href="/huong-dan">
                                    <IntlMessages id="bsc.label.huongdansudung" text="Hướng dẫn sử dụng" />
                                </Link>
                            ),
                            icon: <span><TipIco /></span>,
                        },

                    ],
                },
            ] as MenuItem[]) : []),
        ])
        : []),

] as MenuItem[];

type NavbarProps = {
    /** Đợi sync profile/role theo phân hệ xong mới filter menu */
    profilePhanHeSyncing?: boolean;
};

export const Navbar: React.FC<NavbarProps> = ({ profilePhanHeSyncing = false }) => {
    const { t } = useTranslation();
    const [isShow, setShowNav] = useRecoilState(menu);
    const [, startTransition] = useTransition();
    const { profile, tenantID } = useAuthStore();
    const isAdmin = Boolean(profile?.isAdmin);
    // Optional AI Agent: chỉ hiện menu khi tenant đã bật «Kích hoạt AI» (tự cập nhật khi đổi cấu hình);
    // không giới hạn admin vì menu còn hiện cho người có quyền kho tri thức (showAiKnowledge).
    const { activeAI } = useActiveAiGate();
    const { enabled: suDungBackupBaoCao } = useSuDungBackupBaoCao();
    const { me: aiMe } = useAiHubMe();
    const aiCoaching = canUseAiCoaching(tenantID, profile, aiMe);
    const isQuanLy = Boolean(profile?.isQuanLy);
    const companyScopeLabel = useMemo(
        () =>
            isAdmin
                ? t('bsc.label.congty', 'Công ty')
                : isQuanLy
                    ? t('bsc.label.phongban', 'Phòng ban')
                    : t('bsc.label.nhanvien', 'Nhân viên'),
        [isAdmin, isQuanLy, t],
    );
    const router = useRouter();
    const phanHeValue = useRecoilValue(phanHe);
    const activePhanHe = useMemo(
        () => resolvePhanHeFromRoute(router.pathname, router.asPath) || phanHeValue || CONSTANTS.PHAN_HE.BSC,
        [router.pathname, router.asPath, phanHeValue],
    );
    const [selectedKeys, setSelectedKeys] = useState<string[]>(['']);
    const [openKeys, setOpenKeys] = useState<string[]>(['']);
    const { clearCache, loading: isClearing } = useClearCache();
    const [license, setLicense] = useState<License | null | undefined>(undefined);
    const customerLogoSrc = useCustomerLogoHeader(tenantID);
    const { enabled: canhBaoKpi3KyEnabled, loading: canhBaoKpi3KyLoading } =
        useCanhBaoKPIBaKyLienTiepEnabled();
    const allowKpiKhongDat = !canhBaoKpi3KyLoading && canhBaoKpi3KyEnabled;
    // Thiết lập › Cấu hình «Sử dụng Action Plan V2»: bật → menu V2, tắt (mặc định) → menu bản cũ
    const { enabled: actionPlanV2Enabled } = useActionPlanV2Enabled();
    const [listRole, setListRole] = useState<any[]>([]);
    const licenseLoadedRef = useRef<string | null>(null);
    const licenseRedirectingRef = useRef(false);
    const switchingPhanHeRef = useRef(false);

    /** Logo → sync profile rồi /intro (đổi PH). Không chạy khi đã ở intro. */
    const goSwitchPhanHe = useCallback(
        async (e?: React.MouseEvent) => {
            e?.preventDefault();
            if (switchingPhanHeRef.current) return;
            if (
                router.pathname === ROUTES.INTRO ||
                router.asPath.startsWith(`${ROUTES.INTRO}/`)
            ) {
                return;
            }

            switchingPhanHeRef.current = true;
            const hideLoadingMsg = message.loading(
                t('bsc.msg.dangloaddulieu', 'Đang tải dữ liệu...'),
                0,
            );
            try {
                await syncLoggedInUserPermissionsAfterRoleGroupChange();
                await router.push(ROUTES.INTRO);
            } catch (error) {
                console.error('[navbar] switch phan he failed', error);
                message.error(t('bsc.msg.loi', 'Có lỗi xảy ra, vui lòng thử lại'));
            } finally {
                hideLoadingMsg();
                switchingPhanHeRef.current = false;
            }
        },
        [router, t],
    );

    const invalidateNotifications = useRealtimeStore((state) => state.invalidate.notifications);
    const notificationData = useRealtimeStore((state) => state.notificationData);

    // Đồng bộ modules theo phân hệ ngay khi license/activePhanHe đổi (VH dùng vhFunction)
    const moduleByLicense = useMemo(() => {
        if (!license || !isLicenseActive(license)) return [];
        return resolveLicenseModules(license, activePhanHe);
    }, [license, activePhanHe]);

    const isLicenseReady = license !== undefined;
    const canRenderMenu =
        isLicenseReady
        && isLicenseActive(license)
        && !profilePhanHeSyncing;

    // Phân hệ license lấy từ thoiGians (key + actived), giao với profile.availablePhanHe
    const { showBSC, showVanHoa, showQuyTrinh } = useMemo(() => {
        const hasPhanHe = (raw: string | null | undefined, key: string) =>
            (raw ?? '')
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean)
                .some((item) => item === key);

        const licensePhanHe = Array.isArray(license?.thoiGians)
            ? [...new Set(
                license.thoiGians
                    .filter((item) => item?.actived && item?.key)
                    .map((item) => String(item.key).trim().toLowerCase())
                    .filter(Boolean),
            )].join(',')
            : '';

        return {
            showBSC: hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.BSC) &&
            (hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.BSC) || Boolean(profile?.isAdminBsc)),
            showVanHoa:
                hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.VAN_HOA) &&
                (hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.VAN_HOA) || Boolean(profile?.isAdminVanHoa)),
            showQuyTrinh:
                hasPhanHe(licensePhanHe, CONSTANTS.PHAN_HE.QUY_TRINH) &&
                (hasAvailablePhanHe(profile, CONSTANTS.PHAN_HE.QUY_TRINH) || Boolean(profile?.isAdminQuyTrinh)),
        };
    }, [profile, license, license?.thoiGians]);

    // Chỉ bật menu VH khi license đã load và có quyền phân hệ VH
    const includeVanHoaMenus = isLicenseReady && activePhanHe === CONSTANTS.PHAN_HE.VAN_HOA && showVanHoa;
    const includeBscMenus = isLicenseReady && activePhanHe === CONSTANTS.PHAN_HE.BSC && showBSC;
    const includeQuyTrinhMenus = isLicenseReady && activePhanHe === CONSTANTS.PHAN_HE.QUY_TRINH && showQuyTrinh;

    console.log('isLicenseReady', isLicenseReady);
    console.log('activePhanHe', activePhanHe);
    console.log('showQuyTrinh', showQuyTrinh);

    // Xác định menu item active dựa trên route hiện tại
    useEffect(() => {
        syncNavbarMenuKeysFromPathname(router.pathname, router.asPath, setSelectedKeys, setOpenKeys, {
            includeVanHoaMenus,
            allowActionPlanV2: canShowActionPlanV2Menu(actionPlanV2Enabled),
            allowKpiKhongDat,
        });
    }, [router.pathname, router.asPath, includeVanHoaMenus, actionPlanV2Enabled, allowKpiKhongDat]);

    useEffect(() => {
        const path = (router.pathname || '').replace(/\/$/, '') || '/';
        if (path === '/bao-cao-nhanh') {
            setShowNav(true);
        }
    }, [router.pathname, setShowNav]);

    // useEffect(() => {
    //     if (router.pathname.startsWith('/van-hoa/dot-danh-gia/tien-do/') && !isShow) {
    //         setShowNav(true);
    //     }
    // }, [isShow, router.pathname, setShowNav]);

    const initPermission = useCallback((_licenseData: License, _phanHe: string) => {
        setListRole(profile?.listRole || []);
    }, [profile?.listRole]);

    const loadLicense = useCallback(async (forceReload = false) => {
        if (!tenantID) return;

        if (!forceReload) {
            if (licenseLoadedRef.current === tenantID) return;

            const cached = readLicenseFromStorage(tenantID);
            if (cached !== undefined && cached !== null) {
                licenseLoadedRef.current = tenantID;
                setLicense(cached);
                if (isLicenseActive(cached)) {
                    initPermission(cached, activePhanHe);
                }
                return;
            }
        } else {
            clearLicenseStorage();
            licenseLoadedRef.current = null;
            setLicense(undefined);
        }

        try {
            const res: any = await getLicenseOnce(tenantID, forceReload);
            if (res?.success) {
                const data = (res.data ?? null) as License | null;
                licenseLoadedRef.current = tenantID;
                setLicense(data);
                saveLicenseToStorage(tenantID, data);
                if (data && isLicenseActive(data)) {
                    initPermission(data, activePhanHe);
                }
            } else {
                licenseLoadedRef.current = null;
                setLicense(null);
            }
        } catch (err) {
            console.error(err);
            licenseLoadedRef.current = null;
            clearLicenseStorage();
            setLicense(null);
        }
    }, [tenantID, activePhanHe, initPermission]);

    const handleClearCache = async () => {
        try {
            await clearCache();
            // useClearCache đã getlicense?init=1 (giống login) và ghi đè storage —
            // sync Navbar từ cache, không clear state trước (tránh nhảy /thiet-lap/license)
            if (tenantID) {
                const cached = readLicenseFromStorage(tenantID);
                if (cached !== undefined && cached !== null) {
                    licenseLoadedRef.current = tenantID;
                    setLicense(cached);
                    if (isLicenseActive(cached)) {
                        initPermission(cached, activePhanHe);
                    }
                }
            }
            message.success(t('bsc.msg.daxoacache', 'Đã xóa cache'));
        } catch (error) {
            console.error('Clear cache error', error);
            message.error(t('bsc.msg.xoacachethatbai', 'Xóa cache thất bại'));
        }
    };

    useEffect(() => {
        setListRole(profile?.listRole || []);
    }, [profile?.listRole]);

    useEffect(() => {
        if (!invalidateNotifications || !notificationData?.lastNotification) return;
        if (notificationData.lastNotification.type !== 'reload_license') return;

        const reloadLicense = async () => {
            licenseLoadedRef.current = null;
            const payload = notificationData.lastNotification?.data as { license?: License } | undefined;
            const nextLicense = payload?.license;

            if (tenantID && nextLicense) {
                saveLicenseToStorage(tenantID, nextLicense);
                licenseLoadedRef.current = tenantID;
                setLicense(nextLicense);
                if (isLicenseActive(nextLicense)) {
                    initPermission(nextLicense, activePhanHe);
                }
                return;
            }

            clearLicenseStorage();
            await clearCache();
            await loadLicense(true);
        };

        void reloadLicense();
    }, [invalidateNotifications, notificationData.lastNotification, tenantID, clearCache, loadLicense, activePhanHe, initPermission]);

    const hasRole = (modulename: string, checklicense: boolean = false) => {
        const arrayModule = String(modulename || '').split(',').map((item) => item.trim()).filter(Boolean);
        const hasLicense = !checklicense
            ? true
            : activePhanHe === CONSTANTS.PHAN_HE.VAN_HOA
                ? matchesVanHoaLicense(arrayModule, moduleByLicense)
                : activePhanHe === CONSTANTS.PHAN_HE.QUY_TRINH
                    ? matchesQuyTrinhLicense(arrayModule, moduleByLicense)
                    : moduleByLicense.some((module: string) => arrayModule.includes(module));
        const rolesForPhanHe = filterListRoleByPhanHe(listRole, activePhanHe);
        const hasRead = isAdmin
            ? true
            : rolesForPhanHe.some((role: any) =>
                activePhanHe === CONSTANTS.PHAN_HE.VAN_HOA
                    ? roleMatchesVanHoaMenuModules(role, arrayModule)
                    : roleMatchesMenuModulesExact(role, arrayModule),
            );
        return hasRead && hasLicense;
    };

    // Helper function để collect tất cả modulename từ children (recursively)
    const getAllModuleNames = (items: MenuItem[]): string[] => {
        const modulenames: string[] = [];
        items.forEach((item) => {
            if (!item || typeof item !== 'object') return;

            const menuItem = item as any;
            if (menuItem.modulename) {
                modulenames.push(menuItem.modulename);
            }

            if (menuItem.children && Array.isArray(menuItem.children)) {
                modulenames.push(...getAllModuleNames(menuItem.children));
            }
        });

        return modulenames;
    };

    const filterMenuByRole = (items: MenuItem[]): MenuItem[] => {
        return items
            .map((item) => {
                if (!item || typeof item !== 'object') return item;

                const menuItem = item as any;
                const itemKey = menuItem.key;
                if (!itemKey) return item;


                // Nếu item có children, collect tất cả modulename từ children để check quyền
                if (menuItem.children && Array.isArray(menuItem.children)) {
                    // Collect tất cả modulename từ children (trước khi filter)
                    const allModuleNames = getAllModuleNames(menuItem.children);

                    // Check quyền dựa trên tất cả modulename từ children
                    // Nếu không có modulename nào, mặc định có quyền (hiển thị)
                    const hasAnyPermission = allModuleNames.length === 0 || allModuleNames.some((modulename) => {
                        // Tìm checklicense từ child tương ứng
                        const childItem = menuItem.children.find((child: any) => child.modulename === modulename);
                        const checklicense = childItem?.checklicense ?? false;
                        // Menu item 'report' (bao-cao-kpis) dùng permission của 'buoc6' thay vì 'buoc11'
                        let modulenameForCheck = modulename;
                        if (childItem?.key === 'report' && modulename.includes('buoc11')) {
                            modulenameForCheck = `${modulename.replace(/buoc11/g, 'buoc6')},buoc6_baocao_kpi`;
                        }
                        // if (childItem?.key === 'du-an' && modulename.includes('buoc9')) {
                        //     modulenameForCheck = modulename.replace(/buoc9/g, 'buoc9');
                        // }
                        return hasRole(modulenameForCheck, checklicense);
                    });

                    // Nếu không có modulename nào có quyền, kiểm tra quyền của chính item đó.
                    // Bỏ qua early-hide khi còn child không có modulename (vd. Hướng dẫn sử dụng) —
                    // các child đó mặc định hiển thị cho mọi role sau bước filter bên dưới.
                    if (!hasAnyPermission && allModuleNames.length > 0) {
                        const hasPublicChild = menuItem.children.some(
                            (child: any) => child && typeof child === 'object' && !child.modulename,
                        );
                        if (!hasPublicChild) {
                            const modulenameToCheck = menuItem.modulename || itemKey;
                            if (!hasRole(modulenameToCheck, menuItem.checklicense)) {
                                return null;
                            }
                        }
                    }

                    // Filter children - nếu child không có modulename, mặc định hiển thị
                    const filteredChildren = filterMenuByRole(menuItem.children);

                    // Nếu không có children nào sau khi filter và item cũng không có quyền, loại bỏ
                    if (filteredChildren.length === 0) {
                        const modulenameToCheck = menuItem.modulename || itemKey;
                        return hasRole(modulenameToCheck, menuItem.checklicense) ? item : null;
                    }

                    // Giữ lại item với children đã filter
                    return {
                        ...item,
                        children: filteredChildren,
                    };
                }

                // Nếu không có children, kiểm tra quyền trực tiếp
                // Nếu không có modulename, mặc định hiển thị
                if (!menuItem.modulename) {
                    return item;
                }

                // Action Plan: chỉ hiện một bản theo setting «Sử dụng Action Plan V2».
                if (itemKey === 'b8-action-plan-v2' && !canShowActionPlanV2Menu(actionPlanV2Enabled)) {
                    return null;
                }
                if (itemKey === 'b8-action-plan' && canShowActionPlanV2Menu(actionPlanV2Enabled)) {
                    return null;
                }

                // KPI không đạt: chỉ hiện khi bật Cảnh báo KPI ba kỳ liên tiếp.
                if (itemKey === 'kpi-khong-dat' && !allowKpiKhongDat) {
                    return null;
                }

                // Menu item 'report' (bao-cao-kpis) dùng permission của 'buoc6' thay vì 'buoc11'
                let modulenameToCheck = menuItem.modulename || itemKey;
                if (itemKey === 'report' && modulenameToCheck.includes('buoc11')) {
                    modulenameToCheck = `${modulenameToCheck.replace(/buoc11/g, 'buoc6')},buoc6_baocao_kpi`;
                }
                return hasRole(modulenameToCheck, menuItem.checklicense) ? item : null;
            })
            .filter((item): item is MenuItem => item !== null);
    };

    // Helper function để chuyển đổi checklicense từ boolean sang string cho DOM
    const convertCheckLicenseToString = (items: MenuItem[]): MenuItem[] => {
        return items.map((item) => {
            if (!item || typeof item !== 'object') return item;

            const menuItem = item as any;
            const converted: any = { ...menuItem };

            // Chuyển đổi checklicense từ boolean sang string
            if ('checklicense' in converted && typeof converted.checklicense === 'boolean') {
                converted.checklicense = converted.checklicense.toString();
            }

            // Xử lý recursive cho children
            if (converted.children && Array.isArray(converted.children)) {
                converted.children = convertCheckLicenseToString(converted.children);
            }

            return converted;
        });
    };

    // Memoize menu items to avoid expensive recomputation on every render (e.g. when toggling menu)
    const { menuItems, alertDemoKeys } = useMemo(() => {
        const filtered = filterMenuByRole(
            baseItems(
                isAdmin,
                includeVanHoaMenus,
                includeBscMenus,
                includeQuyTrinhMenus,
                Boolean(profile?.isAdminQuyTrinh),
                canManageKnowledge(tenantID, profile),
                activeAI,
                aiCoaching,
                suDungBackupBaoCao,
            ),
        );
        const demoKeys = collectAlertDemoKeys(filtered);
        const isDemo = isLicenseDemo(license);
        const items = stripAlertDemoProp(convertCheckLicenseToString(disableAlertDemoNavigation(filtered, isDemo))).map((group) => {
            if (group && typeof group === 'object' && 'children' in (group as any)) {
                const g = { ...(group as any) };
                if (Array.isArray(g.children)) {
                    g.children = g.children.map((item: any) => {
                        if (item?.key === 'b4-ban-do-chien-luoc' && Array.isArray(item.children)) {
                            const cloned = { ...item };
                            cloned.children = item.children.map((child: any) => {
                                if (child?.key === 'b4-cong-ty') {
                                    return { ...child, label: <Link href="/ban-do-chien-luoc">{companyScopeLabel}</Link> };
                                }
                                return child;
                            });
                            return cloned;
                        }
                        return item;
                    });
                }
                return g;
            }
            return group;
        });

        return { menuItems: items, alertDemoKeys: demoKeys };
    }, [
        isAdmin,
        includeVanHoaMenus,
        includeBscMenus,
        includeQuyTrinhMenus,
        profile?.isAdminQuyTrinh,
        profile?.isAdminBsc,
        moduleByLicense,
        listRole,
        companyScopeLabel,
        license,
        tenantID,
        allowKpiKhongDat,
        activeAI,
        aiCoaching,
        suDungBackupBaoCao,
    ]);

    const onClick = useCallback<NonNullable<MenuProps['onClick']>>((e) => {
        if (alertDemoKeys.has(e.key) && isLicenseDemo(license)) {
            e.domEvent?.preventDefault();
            e.domEvent?.stopPropagation();
            showDemoAccountBlockedModal(t);
            return;
        }

        // Xử lý riêng cho B9 Phong Cách & Văn Hóa
        if (e.key === 'b9-phong-cach-van-hoa') {
            e.domEvent?.preventDefault();
            e.domEvent?.stopPropagation();
            window.dispatchEvent(
                new CustomEvent('open-help-modal', {
                    detail: { controller: 'phongcach' },
                }),
            );
        }
    }, [alertDemoKeys, license, t]);

    // Note: Message handlers removed - use RealtimeStore for data invalidation
    // For real-time messages, consider using a separate message store if needed

    useEffect(() => {
        if (!tenantID) return;
        if (licenseLoadedRef.current === tenantID) return;

        const cached = readLicenseFromStorage(tenantID);
        void loadLicense(cached === undefined);
    }, [tenantID, loadLicense]);

    // Redirect khi license chưa hợp lệ; bỏ qua trang thiết lập license để tránh loop
    useEffect(() => {
        if (!tenantID || license === undefined) return;
        if (isLicenseActive(license)) return;
        if (isOnLicenseSetupPage(router.pathname)) return;
        if (router.pathname === ROUTES.INTRO || router.asPath.startsWith(`${ROUTES.INTRO}/`)) return;
        if (licenseRedirectingRef.current) return;

        licenseRedirectingRef.current = true;
        void router.replace(LICENSE_SETUP_PATH).finally(() => {
            licenseRedirectingRef.current = false;
        });
    }, [license, tenantID, router.pathname, router.asPath, router]);

    return (
        <>
            <div className="py-2 pl-2 relative z-40 shrink-0" style={{ contain: 'layout' }}>
                <div className="flex flex-col bg-white rounded-2xl">
                    {/* Logo — sync profile rồi vào /intro đổi PH */}
                    <div className="flex justify-center items-center px-4 relative h-[90px]">
                        <Link
                            href={ROUTES.INTRO}
                            onClick={(e) => {
                                void goSwitchPhanHe(e);
                            }}
                            className="flex transition-all duration-200 ease-in-out"
                        >
                            <div className="transition-all duration-200 ease-in-out will-change-[width,height,opacity]">
                                <Image
                                    src="/images/logo.png"
                                    alt="globalmap"
                                    className={`${isShow ? 'block' : 'hidden'}`}
                                    width={40}
                                    height={40}
                                />
                                <Image
                                    src="/images/logo-main.png"
                                    alt="globalmap"
                                    className={`${isShow ? 'hidden' : 'block'}`}
                                    width={100}
                                    height={95}
                                />
                            </div>
                            {customerLogoSrc && !isShow ? (
                                <img
                                    src={customerLogoSrc}
                                    alt="logo-customer"
                                    className="object-contain max-h-20 max-w-[100px]"
                                />
                            ) : null}
                        </Link>

                        {/* toggle button */}
                        <div
                            className={`rounded-full absolute top-1/2 -translate-y-1/2 w-[26px] h-[26px] hover:border-primary transition-all duration-200 ease-in-out
                border-1 border-gray-300 flex items-center justify-center cursor-pointer bg-white ${!isShow ? 'right-[-13px]' : 'right-[-13px]'
                                }`}
                            onClick={() => {
                                setMenuTransitioning(true, 200);
                                startTransition(() => setShowNav(!isShow));
                            }}
                        >
                            {!isShow ? <ChevronLeft width={16} height={16} /> : <ChevronRight width={16} height={16} />}
                        </div>
                    </div>

                    <div
                        className={`h-[calc(100vh-88px)] max-h-screen transition-all duration-200 ease-in-out custom-thin-scrollbar ${!isShow ? 'overflow-y-scroll w-[280px]' : 'overflow-y-scroll w-[87px]'
                            }`}
                    >
                        <Menu
                            onClick={onClick}
                            className="!border-r-0 transition-all duration-200 ease-in-out"
                            selectedKeys={selectedKeys}
                            mode="inline"
                            style={{ paddingBottom: isShow ? '30px' : '0px' }}
                            inlineCollapsed={isShow}
                            triggerSubMenuAction="hover"
                            {...(!isShow ? { openKeys, onOpenChange: setOpenKeys } : {})}
                            items={canRenderMenu ? menuItems : []}
                        />

                        {!isShow && (
                            <div className="flex flex-col items-center justify-center pt-10 pb-20 gap-y-2 px-4">
                                <Image
                                    src="/images/license.svg"
                                    alt="globalmap"
                                    className="object-contain"
                                    width={60}
                                    height={60}
                                    style={{ width: 'auto', height: 'auto' }}
                                />
                                <div className="flex flex-col items-center">
                                    <p>
                                        <Link href='/thiet-lap/license'>
                                            {t('bsc.label.license', 'License')}
                                        </Link>
                                    </p>
                                    <p className="font-semibold text-919EAB">
                                        <Link href='/thiet-lap/license' className='text-blue-500 hover:underline'>
                                            {license?.key}
                                        </Link>
                                    </p>
                                </div>
                                {isLicenseActive(license) && (
                                    <Button
                                        type="primary"
                                        className="!bg-1c252e text-white"
                                        loading={isClearing}
                                        onClick={handleClearCache}
                                    >
                                        {t('bsc.btn.clearcache', 'Clear Cache')}
                                    </Button>) }
                            </div>
                        )}

                        <div className="flex flex-col items-center justify-center pb-5 gap-y-2 px-4">
                            <VersionBadge />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
